<template>
  <div>
    <b-row>
      <b-col
        xs="12"
        sm="6"
        md="8"
        lg="8"
        xl="9"
        class="d-flex justify-content-between pb-1 align-items-center"
      >
        <div class="d-flex w-100 justify-content-between">
          <h1>
            {{ $t(titleKey) }}
          </h1>
        </div>
      </b-col>
      <b-col
        xs="12"
        sm="6"
        md="4"
        lg="4"
        xl="3"
      >
        <income-total-card
          :label="'totalExcomes'"
          :total-income="total"
        />
      </b-col>
    </b-row>
    <b-row>
      <b-col
        sm="12"
        class="mb-2"
      >
        <div class="d-flex align-items-center">
          <span class="mr-2">{{ $t('show') }} </span>
          <b-form-select
            :value="expensesPagination.per_page"
            style="width:100px"
            :options="perPageOptions"
            @change="changePerPageValue($event)"
          />
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col
        cols="6"
        sm="6"
        lg="3"
      >
        <b-form-group :label="$t('fromDate')">
          <b-form-datepicker
            v-model="query.dates.begin"
            reset-button
            @input="filterExpenses"
          />
        </b-form-group>
      </b-col>
      <b-col
        cols="6"
        sm="6"
        lg="3"
      >
        <b-form-group :label="$t('toDate')">
          <b-form-datepicker
            v-model="query.dates.end"
            reset-button
            @input="filterExpenses"
          />
        </b-form-group>
      </b-col>
      <b-col
        cols="6"
        sm="6"
        lg="3"
      >
        <b-form-group :label="$t('status')">
          <b-form-select
            v-model="query.status"
            :options="expenseStatusOptions"
            @change="filterExpenses"
          />
        </b-form-group>
      </b-col>
      <b-col
        cols="6"
        sm="6"
        lg="3"
      >
        <b-form-group :label="$t('name')">
          <b-form-input
            v-model="query.name"
            autocomplete="off"
            type="text"
            @input="onSearchInput"
          />
        </b-form-group>
      </b-col>
      <b-col
        cols="6"
        sm="6"
        lg="3"
      >
        <b-form-group :label="$t('supplier')">
          <b-form-select
            v-model="query.supplier"
            :options="supplierOptions"
            @change="filterExpenses"
          />
        </b-form-group>
      </b-col>
      <!-- Filtro por categoría -->
      <b-col
        cols="6"
        sm="6"
        lg="3"
        v-if="showCategoryFilters"
      >
        <b-form-group :label="$t('category')">
          <b-form-select
            v-model="query.expensetypecatalog"
            :options="expensetypecatalogOptions"
            @change="onCategoryChange(query.expensetypecatalog)"
          />
        </b-form-group>
      </b-col>

      <!-- Filtro por subcategorías dinámicas -->
      <template v-if="showCategoryFilters">
        <b-col
          v-for="(subcategoryOptions, index) in subcategories"
          :key="index"
          cols="9"
          sm="6"
          lg="3"
        >
          <b-form-group :label="$t('subcategory') + ' ' + (index + 1)">
            <b-form-select
              v-model="selectedSubcategories[index]"
              :options="subcategoryOptions.map(subcat => ({ text: subcat.name, value: subcat.id }))"
              @change="onSubcategoryChange(selectedSubcategories[index], index)"
            />
          </b-form-group>
        </b-col>
      </template>
    </b-row>
    <b-row>
      <b-col>
        <b-overlay :show="loading">
          <expenses-table
            :is-material="false"
            :expenses="expenses"
            :project-id="scopeProjectId"
            :customer-id="scopeCustomerId"
            :show-project-column="showProjectColumn"
            :grouped-expense-project-map="groupedExpenseProjectMap"
            @sort-changed="handleSortChange"
          />
        </b-overlay>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="d-flex justify-content-end">
        <b-pagination
          :value="expensesPagination.page"
          :total-rows="expensesPagination.total_objects"
          :per-page="expensesPagination.per_page"
          aria-controls="my-table"
          @change="nextPage($event)"
        />
      </b-col>
    </b-row>
    <div
      v-if="scopeProjectId"
      class="float-button"
    >
      <b-button
        :to="{
          name: 'newExpense',
          params: { projectId: scopeProjectId },
          query: { createType: 'direct_cost' },
        }"
        variant="primary"
        class="add-button"
      >
        <PlusIcon size="2x" />
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { PlusIcon } from 'vue-feather-icons'
import ExpensesTable from '@/components/expenses/ExpensesTable.vue'
import IncomeTotalCard from '@/components/income/IncomeTotalCard.vue'

const DEFAULT_SUPPLIER = 'YoConstruyo'

export default {
  components: {
    ExpensesTable,
    IncomeTotalCard,
    PlusIcon,
  },
  props: {
    projectId: {
      type: [String, Number],
      default: null,
    },
    customerId: {
      type: [String, Number],
      default: null,
    },
    titleKey: {
      type: String,
      default: 'expenses',
    },
    showProjectColumn: {
      type: Boolean,
      default: false,
    },
    groupedExpenseProjectMap: {
      type: Object,
      default() {
        return {}
      },
    },
  },
  data() {
    return {
      loading: false,
      sortBy: 'expense_date',
      sortDesc: true,
      searchTimer: null,
      perPageOptions: [
        { value: 10, text: '10' },
        { value: 20, text: '20' },
        { value: 30, text: '30' },
        { value: 40, text: '40' },
        { value: 50, text: '50' },
        { value: 60, text: '60' },
        { value: 70, text: '70' },
        { value: 99999, text: this.$t('all') }, // opcionalmente puedes usar 'all'
      ],
      expenseStatusOptions: [
        { text: this.$t('all'), value: null },
        { text: this.$t('paid'), value: 'paid' },
        { text: this.$t('credit'), value: 'credit' },
        { text: this.$t('expired'), value: 'expired' },
        { text: this.$t('topay'), value: 'topay' },
      ],
      expensetypecatalogOptions: [
        { text: this.$t('all'), value: null },
      ],
      supplierOptions: [
        { text: this.$t('all'), value: null },
      ],
      subcategories: [], // Array para almacenar niveles de subcategorías
      selectedSubcategories: [], // Almacenar los valores seleccionados para cada nivel
      query: {
        dates: {},
        status: null,
        supplier: null,
        name: null,
        expensetypecatalog: null, // Será "Todos" si es null
        subcategory: null, // Subcategoría seleccionada
      },
    }
  },
  computed: {
    ...mapGetters('projects', ['project']),
    ...mapGetters('expenses', [
      'expenses',
      'expensesPagination',
      'expensesTotal',
    ]),
    scopeProjectId() {
      return this.projectId || this.$route.params.projectId || null
    },
    scopeCustomerId() {
      return this.customerId || this.$route.params.customerId || null
    },
    showCategoryFilters() {
      return !!this.scopeProjectId
    },
    total() {
      return `$${this.$options.filters.money(this.expensesTotal)}`
    },
    isMaterial() {
      return this.expenses[0]?.expense_type_catalog.name === 'Material'
    },
  },

  mounted() {
    this.loading = true
    const { expensetypecatalog } = this.$route.query

    this.loadSuppliers()

    if (this.showCategoryFilters) {
      this.fetchExpensesTypeCatalogs(this.scopeProjectId).then(async catalogs => {
      // Setear opciones del combo principal
        this.expensetypecatalogOptions = [
          { text: this.$t('all'), value: null },
          ...catalogs.data.map(cat => ({
            text: cat.name,
            value: cat.id,
            subcategories: cat.subcategories || [],
          })),
        ]

        if (expensetypecatalog) {
          const path = this.findCategoryPath(catalogs.data, expensetypecatalog)
          if (path && path.length) {
            const root = path[0]
            this.query.expensetypecatalog = root.id
            this.subcategories = []
            this.selectedSubcategories = []

            path.slice(1).forEach((node, index) => {
              const parent = path[index]
              const subs = parent.subcategories || parent.children || []
              if (subs.length) {
                this.subcategories.push(subs)
              }
              this.selectedSubcategories.push(node.id)
            })

            const last = path[path.length - 1]
            const lastSubs = last.subcategories || last.children || []
            if (lastSubs.length) {
              this.subcategories.push(lastSubs)
            }

            this.filterExpenses()
          } else {
            this.filterExpenses()
          }
        } else {
          this.filterExpenses()
        }
      }).finally(() => {
        this.loading = false
      })
    } else {
      this.filterExpenses()
    }
  },
  // watch: {
  //   // // Actualiza las categorías disponibles cuando cambian los datos de los gastos
  //   // expenses(newExpenses) {
  //   //   const allCategories = this.getUniqueExpenseTypeCatalogs(newExpenses)
  //   //   this.expensetypecatalogOptions = [
  //   //     { text: this.$t('all'), value: null },
  //   //     ...allCategories.map(name => ({ text: name, value: name })),
  //   //   ]
  //   // },
  // },
  methods: {
    ...mapMutations('expenses', {
      changePagination: 'CHANGE_PAGINATION',
    }),
    ...mapActions('expenses', ['fetchExpenses', 'fetchSuppliers']),
    ...mapActions('groupedExpenses', ['fetchExpensesTypeCatalogs']),
    async initializeFromSubcategory(subcategoryId) {
      const catalogs = await this.fetchExpensesTypeCatalogs(this.scopeProjectId)
      const path = this.findCategoryPath(catalogs.data, subcategoryId)
      if (path && path.length) {
        const root = path[0]
        this.query.expensetypecatalog = root.id
        this.subcategories = []
        this.selectedSubcategories = []

        path.slice(1).forEach((node, index) => {
          const parent = path[index]
          const subs = parent.subcategories || parent.children || []
          if (subs.length) {
            this.subcategories.push(subs)
          }
          this.selectedSubcategories.push(node.id)
        })

        const last = path[path.length - 1]
        const lastSubs = last.subcategories || last.children || []
        if (lastSubs.length) {
          this.subcategories.push(lastSubs)
        }

        this.filterExpenses()
      }
    },
    changePerPageValue(value) {
      this.changePagination({ field: 'per_page', value })
      this.filterExpenses({ resetPage: true })
    },
    // Metodo para mostrar paginacion de egresos
    nextPage(page) {
      this.changePagination({ field: 'page', value: page })
      this.filterExpenses({ resetPage: false })
    },
    // Metodo para actualizar datos de cabecera
    handleSortChange({ sortBy, sortDesc }) {
      this.sortBy = sortBy
      this.sortDesc = sortDesc
      this.filterExpenses({ resetPage: true })
    },
    onSearchInput() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.filterExpenses({ resetPage: true })
      }, 400)
    },
    loadSuppliers() {
      const projectId = this.scopeProjectId
      const customerId = this.scopeCustomerId
      return this.fetchSuppliers({ projectId, customerId, column: 'supplier' })
        .then(response => {
          const list = Array.isArray(response) ? response : response?.data || []
          const normalized = list
            .map(item => {
              if (typeof item === 'string') return item
              return item?.supplier || item?.name || item?.text || item?.value
            })
            .filter(Boolean)
          const unique = [...new Set(normalized)]
          if (!unique.includes(DEFAULT_SUPPLIER)) {
            unique.unshift(DEFAULT_SUPPLIER)
          }
          this.supplierOptions = [
            { text: this.$t('all'), value: null },
            ...unique.map(name => ({ text: name, value: name })),
          ]
        })
        .catch(error => {
          console.error('Error al obtener proveedores:', error)
        })
    },
    findCategoryPath(list, targetId, trail = []) {
      let result = null
      list.some(item => {
        const nextTrail = [...trail, item]
        if (item.id === targetId) {
          result = nextTrail
          return true
        }

        const subs = item.subcategories || item.children || []
        if (subs.length) {
          const found = this.findCategoryPath(subs, targetId, nextTrail)
          if (found) {
            result = found
            return true
          }
        }

        return false
      })
      return result
    },
    ...mapActions('groupedExpenses', ['fetchSubExpenseTypeCatalog']),
    onCategoryChange(selectedCategoryId) {
      this.query.expensetypecatalog = selectedCategoryId
      this.subcategories = []
      this.selectedSubcategories = []

      // Llamada al endpoint para obtener subcategorías
      if (selectedCategoryId) {
        this.fetchSubExpenseTypeCatalog({ projectId: this.scopeProjectId, expenseTypeId: selectedCategoryId })
          .then(category => {
            if (category.subcategories && category.subcategories.length) {
              this.subcategories = [...this.subcategories, category.subcategories]
            }
            this.filterExpenses({ resetPage: true }) // Filtrar gastos después de obtener subcategorías
          })
          .catch(error => {
            console.error('Error al obtener subcategorías:', error)
          })
      } else {
        this.filterExpenses({ resetPage: true }) // Si no se selecciona ninguna categoría, se filtran los gastos
      }
    },

    // Método para manejar la selección de subcategorías
    onSubcategoryChange(selectedSubcategoryId, level) {
      this.selectedSubcategories[level] = selectedSubcategoryId
      this.subcategories.splice(level + 1, this.subcategories.length) // Limpiar subcategorías de niveles inferiores

      if (selectedSubcategoryId) {
        this.fetchSubExpenseTypeCatalog({ projectId: this.scopeProjectId, expenseTypeId: selectedSubcategoryId })
          .then(category => {
            if (category.subcategories && category.subcategories.length) {
              this.subcategories.push(category.subcategories) // Añadir nuevas subcategorías
            }
            this.filterExpenses({ resetPage: true }) // Filtrar gastos después de la selección de la subcategoría
          })
          .catch(error => {
            console.error('Error al obtener subcategorías:', error)
          })
      } else {
        this.filterExpenses({ resetPage: true }) // Si no se seleccionó ninguna subcategoría, se filtran los gastos
      }
    },

    getSortColumn() {
      if (!this.sortBy) return null
      const allowed = [
        'expense_date',
        'name',
        'total',
        'expense_status',
        'supplier',
        'quantity',
        'unit_price',
        'unit_type',
        'quote_folio',
      ]
      return allowed.includes(this.sortBy) ? this.sortBy : null
    },
    filterExpenses(options = {}) {
      let resetPage = true
      if (
        typeof options === 'object'
        && options !== null
        && Object.prototype.hasOwnProperty.call(options, 'resetPage')
      ) {
        resetPage = options.resetPage
      }
      if (resetPage) {
        this.changePagination({ field: 'page', value: 1 })
      }
      let dates = {}
      if (this.query.dates.begin?.length) {
        dates.start_date = this.query.dates.begin
      }
      if (this.query.dates.end?.length) {
        dates.end_date = this.query.dates.end
      }
      if (!Object.keys(dates).length) {
        dates = null
      }

      let categoryIds = null
      if (this.showCategoryFilters) {
        // Función recursiva para obtener todos los IDs de subcategorías anidadas
        const getAllSubcategoryIds = category => {
          let ids = []
          if (category && category.id) {
            ids.push(category.id) // Añadir el ID de la categoría actual solo si existe
          }
          if (category.subcategories && category.subcategories.length > 0) {
            category.subcategories.forEach(subcat => {
              ids = ids.concat(getAllSubcategoryIds(subcat)) // Llamada recursiva para añadir IDs de subcategorías
            })
          }
          return ids
        }

        // Iniciar el array con la categoría principal seleccionada
        categoryIds = []

        // Si se selecciona "Todos" (que tiene el valor null), enviar null a la API
        if (this.query.expensetypecatalog === null) {
          categoryIds = null // Enviar null para que la API devuelva todos los egresos
        } else {
          // Si no se ha seleccionado una subcategoría, incluir la categoría principal
          if (!this.selectedSubcategories.length && this.query.expensetypecatalog) {
            categoryIds.push(this.query.expensetypecatalog) // Añadir el ID principal directamente si no hay subcategorías seleccionadas
          }

          // Verificar si la categoría seleccionada tiene subcategorías y agregar sus IDs
          const selectedCategory = this.expensetypecatalogOptions.find(cat => cat.value === this.query.expensetypecatalog)

          if (selectedCategory && selectedCategory.subcategories && selectedCategory.subcategories.length > 0) {
            const subcategoryIds = getAllSubcategoryIds(selectedCategory)
            categoryIds = categoryIds.concat(subcategoryIds) // Agregar las subcategorías de la categoría seleccionada
          }

          // Agregar también las subcategorías seleccionadas y sus anidadas
          this.selectedSubcategories.forEach((subcategoryId, index) => {
            const subcategory = this.subcategories[index]?.find(subcat => subcat.id === subcategoryId)
            if (subcategory) {
              categoryIds = getAllSubcategoryIds(subcategory) // Reemplazar categoryIds con subcategorías anidadas, sin añadir la principal
            }
          })

          // Eliminar duplicados del array de IDs
          categoryIds = [...new Set(categoryIds)] // Usar Set para eliminar duplicados
        }
      }

      // Si se selecciona "Todos" o no hay IDs, permitir la llamada a la API
      if (categoryIds === null || categoryIds.length > 0) {
        this.loading = true
        const column = this.getSortColumn()
        let order = null
        if (column) {
          order = this.sortDesc ? 'DESC' : 'ASC'
        }
        this.fetchExpenses({
          projectId: this.scopeProjectId,
          customerId: this.scopeCustomerId,
          supplier: this.query.supplier,
          name: this.query.name,
          status: this.query.status,
          dates,
          expensetypecatalog: categoryIds, // Enviar null o array de IDs de categorías y subcategorías
          column,
          order,
          pagination: this.expensesPagination,
        }).finally(() => {
          this.loading = false
        })
      } else {
        console.error('No se seleccionaron categorías o subcategorías')
      }
    },
  },
}
</script>

<style>
.float-button {
  position: fixed;
  bottom: 70px;
  right: 30px;
  z-index: 99;
}

.add-button{
  border-radius: 50% !important;
  padding: 0;
  width: 62px;
  height: 62px;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
